import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { TrendingUp, Sparkles } from 'lucide-react';
import { db } from '../db';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RunwayProjection = ({ dailyBudget, spent }) => {
  const [projection, setProjection] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [tripData, setTripData] = useState({ totalBudget: 0, tripDuration: 30, currency: 'USD', destination: '' });

  useEffect(() => {
    loadTripData();
  }, []);

  useEffect(() => {
    if (tripData.totalBudget > 0) {
      calculateProjection();
    }
  }, [tripData, spent]);

  const loadTripData = async () => {
    try {
      const totalBudgetSetting = await db.settings.get('total_budget');
      const tripDurationSetting = await db.settings.get('trip_duration');
      const currencySetting = await db.settings.get('currency');
      const destinationSetting = await db.settings.get('destination');
      
      setTripData({
        totalBudget: parseFloat(totalBudgetSetting?.value || 0),
        tripDuration: parseInt(tripDurationSetting?.value || 30),
        currency: currencySetting?.value || 'USD',
        destination: destinationSetting?.value || 'Current Location'
      });
    } catch (error) {
      console.error('Failed to load trip data:', error);
    }
  };

  const calculateProjection = () => {
    if (tripData.totalBudget <= 0 || spent <= 0) return;

    const dailyAverage = spent;
    const remainingBudget = tripData.totalBudget - spent;

    // Calculate how many days the remaining budget will last at current spend rate
    const daysRemaining = dailyAverage > 0 ? Math.floor(remainingBudget / dailyAverage) : tripData.tripDuration;
    
    // Project budget remaining at 30/60/90 days if spending continues at current rate
    const budget30 = tripData.totalBudget - (dailyAverage * 30);
    const budget60 = tripData.totalBudget - (dailyAverage * 60);
    const budget90 = tripData.totalBudget - (dailyAverage * 90);

    setProjection({
      daysRemaining,
      budget30: Math.max(0, budget30),
      budget60: Math.max(0, budget60),
      budget90: Math.max(0, budget90),
      runOut30: budget30 < 0,
      runOut60: budget60 < 0,
      runOut90: budget90 < 0,
    });
  };

  const getAIAnalysis = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/budget/analyze`, {
        total_budget: tripData.totalBudget,  // Use actual total budget from settings
        currency: tripData.currency,
        daily_burn_rate: spent,
        current_location: tripData.destination,
        days_traveled: 1,
      });

      setAnalysis(response.data.analysis);
      toast.success('AI analysis complete');
    } catch (error) {
      console.error('AI analysis failed:', error);
      toast.error('Analysis unavailable');
    } finally {
      setLoading(false);
    }
  };

  if (!projection) return null;

  return (
    <div className="space-y-4">
      <Card data-testid="runway-projection-card" className="bg-gradient-to-br from-card to-secondary/50 border-primary/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            Full Runway Projection
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Trip Budget: {tripData.currency} {tripData.totalBudget.toLocaleString()}
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Days remaining at current rate */}
          <div className="text-center py-3 bg-secondary/30 rounded-lg">
            <p className="text-sm text-muted-foreground">Days remaining at current rate</p>
            <p className="text-4xl font-heading font-bold text-primary">{projection.daysRemaining}</p>
          </div>

          {/* Budget projections */}
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center">
              <p className="text-xs text-muted-foreground">After 30 days</p>
              <p className={`text-xl font-heading font-bold ${projection.runOut30 ? 'text-destructive' : 'text-primary'}`}>
                {projection.runOut30 ? 'Empty' : `$${projection.budget30.toFixed(0)}`}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-muted-foreground">After 60 days</p>
              <p className={`text-xl font-heading font-bold ${projection.runOut60 ? 'text-destructive' : 'text-primary'}`}>
                {projection.runOut60 ? 'Empty' : `$${projection.budget60.toFixed(0)}`}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-muted-foreground">After 90 days</p>
              <p className={`text-xl font-heading font-bold ${projection.runOut90 ? 'text-destructive' : 'text-primary'}`}>
                {projection.runOut90 ? 'Empty' : `$${projection.budget90.toFixed(0)}`}
              </p>
            </div>
          </div>

          <Button
            data-testid="get-ai-analysis-button"
            onClick={getAIAnalysis}
            disabled={loading}
            className="w-full rounded-full bg-primary text-primary-foreground"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            {loading ? 'Analyzing...' : 'Get AI Budget Analysis'}
          </Button>
        </CardContent>
      </Card>

      {analysis && (
        <Card data-testid="ai-analysis-card" className="bg-card border-border">
          <CardHeader>
            <CardTitle>AI Budget Analysis</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground whitespace-pre-wrap">{analysis}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default RunwayProjection;